//import './wdyr';

import MainLayout from '@jonni/core/ui/layout/layout.base';
import { queryClient } from '@jonni/lib/queryClient';
import theme from '@jonni/theme/theme';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { Location } from '@reach/router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';

interface AppProps {
  element: React.ReactElement<{
    titleKey?: string;
    pathname?: string;
  }>;
}

/**
 * Main application component.
 *
 * Performance Optimization:
 * - The providers that do not depend on the current URL (location) are placed outside
 *   the <Location> render-prop. This ensures that only the part of the tree that really needs
 *   to update (i.e. the cloned element receiving the updated pathname) is re-rendered on URL changes.
 *
 * Note:
 * - Duplicate logs from development (via React.StrictMode) are expected and desired per your setup.
 * - Bugsnag configuration remains unchanged in this environment.
 */
const App: React.FC<AppProps> = ({ element }) => {
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <HelmetProvider>
              <Toaster
                expand
                position="bottom-left"
                richColors
                toastOptions={{
                  style: {
                    fontFamily: theme.typography.fontFamily,
                  },
                }}
              />
              <Location>{({ location }) => <MainLayout pathName={location.pathname}>{element}</MainLayout>}</Location>
            </HelmetProvider>
          </ThemeProvider>
        </StyledEngineProvider>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </React.StrictMode>
  );
};

export default App;
